import { ALERT_ACTIONS } from '../../constants'
import { WorkflowActionTypes } from '../workflows/views/createWorkflows/types'
import type { TagsProps } from '@lightbeamai/design-system'

export enum SubAlertActionState {
  AwaitingApproval = 'AWAITING_APPROVAL',
  Open = 'OPEN',
  InProgress = 'IN_PROGRESS',
  InReview = 'IN_REVIEW',
  ReviewCompleted = 'REVIEW_COMPLETED',
  OnHold = 'ON_HOLD',
  DetectionConfirmed = 'DETECTION_CONFIRMED',
  IncorrectDetection = 'INCORRECT_DETECTION',
  Approved = 'APPROVED'
}

export const SubAlertActionStateConfig: {
  [key in SubAlertActionState]: {
    color: TagsProps['color']
    intlId: string
  }
} = {
  [SubAlertActionState.Open]: { color: 'information', intlId: 'alerts.subalert.state.open' },
  [SubAlertActionState.InProgress]: { color: 'cyan', intlId: 'alerts.subalert.state.inProgress' },
  [SubAlertActionState.InReview]: { color: 'cyan', intlId: 'alerts.subalert.state.inReview' },
  [SubAlertActionState.ReviewCompleted]: {
    color: 'success',
    intlId: 'alerts.subalert.state.reviewCompleted'
  },
  [SubAlertActionState.OnHold]: { color: 'error', intlId: 'alerts.subalert.state.onHold' },
  [SubAlertActionState.DetectionConfirmed]: {
    color: 'information',
    intlId: 'alerts.subalert.state.detectionConfirmed'
  },
  [SubAlertActionState.IncorrectDetection]: {
    color: 'error',
    intlId: 'alerts.subalert.state.incorrectDetection'
  },
  [SubAlertActionState.Approved]: { color: 'success', intlId: 'alerts.subalert.state.approved' },
  [SubAlertActionState.AwaitingApproval]: {
    color: 'success',
    intlId: 'alerts.subalert.state.awaitingApproval'
  }
}

export const ExtendedSubAlertActionState = {
  ...SubAlertActionState,
  DepartmentApproved: 'DEPARTMENT_APPROVED',
  LegalHold: 'LEGAL_HOLD',
  LegalConcurrence: 'LEGAL_CONCURRENCE',
  IgHold: 'IG_HOLD',
  IgClearance: 'IG_CLEARANCE',
  ComplianceHold: 'COMPLIANCE_HOLD',
  ComplianceClearance: 'COMPLIANCE_CLEARANCE',
  AwaitingApproval: 'AWAITING_APPROVAL'
} as const

export const ExtendedSubAlertActionStateConfig: {
  [key in SubAlertActionState & typeof ExtendedSubAlertActionState]: {
    color: TagsProps['color']
    intlId: string
  }
} = {
  ...SubAlertActionStateConfig,
  [ExtendedSubAlertActionState.DepartmentApproved]: {
    color: 'success',
    intlId: 'alerts.subalert.state.departmentApproved'
  },
  [ExtendedSubAlertActionState.LegalHold]: {
    color: 'cream',
    intlId: 'alerts.subalert.state.legalHold'
  },
  [ExtendedSubAlertActionState.IgHold]: {
    color: 'cream',
    intlId: 'alerts.subalert.state.igHold'
  },
  [ExtendedSubAlertActionState.ComplianceHold]: {
    color: 'cream',
    intlId: 'alerts.subalert.state.complianceHold'
  },
  [ExtendedSubAlertActionState.AwaitingApproval]: {
    color: 'cream',
    intlId: 'alerts.subalert.state.awaitingApproval'
  },
  [ExtendedSubAlertActionState.LegalConcurrence]: {
    color: 'success',
    intlId: 'alerts.subalert.state.legalConcurrence'
  },
  [ExtendedSubAlertActionState.IgClearance]: {
    color: 'success',
    intlId: 'alerts.subalert.state.igClearance'
  },
  [ExtendedSubAlertActionState.ComplianceClearance]: {
    color: 'success',
    intlId: 'alerts.subalert.state.complianceClearance'
  }
} as const

export const SubAlertActionStateFilter = {
  [SubAlertActionState.Open]: 'Open',
  [SubAlertActionState.InProgress]: 'InProgress',
  [SubAlertActionState.InReview]: 'InReview',
  [SubAlertActionState.ReviewCompleted]: 'ReviewCompleted',
  [SubAlertActionState.OnHold]: 'OnHold',
  [SubAlertActionState.DetectionConfirmed]: 'DetectionConfirmed',
  [SubAlertActionState.IncorrectDetection]: 'IncorrectDetection',
  [SubAlertActionState.Approved]: 'Approved',
  [SubAlertActionState.AwaitingApproval]: 'AwaitingApproval'
}

export const ExtendedSubAlertActionStateFilter: {
  [key in SubAlertActionState & typeof ExtendedSubAlertActionState]: string
} = {
  [SubAlertActionState.Open]: 'Open',
  [SubAlertActionState.InProgress]: 'InProgress',
  [SubAlertActionState.InReview]: 'InReview',
  [SubAlertActionState.ReviewCompleted]: 'ReviewCompleted',
  [SubAlertActionState.OnHold]: 'OnHold',
  [SubAlertActionState.DetectionConfirmed]: 'DetectionConfirmed',
  [SubAlertActionState.IncorrectDetection]: 'IncorrectDetection',
  [SubAlertActionState.Approved]: 'Approved',
  [SubAlertActionState.AwaitingApproval]: 'AwaitingApproval',
  [ExtendedSubAlertActionState.DepartmentApproved]: 'DepartmentApproved',
  [ExtendedSubAlertActionState.LegalHold]: 'LegalHold',
  [ExtendedSubAlertActionState.LegalConcurrence]: 'LegalConcurrence',
  [ExtendedSubAlertActionState.IgHold]: 'IGHold',
  [ExtendedSubAlertActionState.IgClearance]: 'IGClearance',
  [ExtendedSubAlertActionState.ComplianceHold]: 'ComplianceHold',
  [ExtendedSubAlertActionState.ComplianceClearance]: 'ComplianceClearance'
}

export const ALERT_ACTIONS_MAPPER = {
  [ALERT_ACTIONS.resolve]: 'alert.action.resolve',
  [ALERT_ACTIONS.reassign]: 'alert.action.reassign',
  [ALERT_ACTIONS.mute]: 'alert.action.mute',
  [ALERT_ACTIONS.unMute]: 'alert.action.unmute',
  [ALERT_ACTIONS.permit]: 'alert.action.value.permit',
  [ALERT_ACTIONS.noScan]: 'alert.action.value.noScan',
  [ALERT_ACTIONS.sendReminder]: 'alert.action.sendReminder',
  [ALERT_ACTIONS.deleteObject]: 'alert.action.value.delete',
  [ALERT_ACTIONS.revokeAccess]: 'alert.action.revokeAccess',
  [ALERT_ACTIONS.archive]: 'alert.action.archive',
  [ALERT_ACTIONS.changeState]: 'alert.action.changeState',
  [ALERT_ACTIONS.create]: 'alert.action.create',
  [ALERT_ACTIONS.update]: 'alert.action.update',
  [ALERT_ACTIONS.delete]: 'alert.action.delete',
  [ALERT_ACTIONS.suspendUser]: 'alert.action.suspendUser',
  [ALERT_ACTIONS.reassignObjectOwners]: 'alert.action.reassignObjectOwners',
  [ALERT_ACTIONS.legalHold]: 'alert.action.legalHold',
  [WorkflowActionTypes.APPLY_LEGAL_HOLD]: 'alert.action.legalHold'
}
